// MARK://dhk8/admin-shell/lib/image-compress
// Normaliza imágenes ANTES de subir al media service, que SOLO acepta
// png/jpeg/webp/gif y máximo 5 MB (services/media storage.service.ts). Sin esto,
// el "a veces carga y a veces no" se explica por: (1) fotos de iPhone en HEIC →
// rechazadas por tipo; (2) fotos de cámara >5 MB → rechazadas por tamaño.
//
// Estrategia: decodificar la imagen y re-exportarla a JPEG reescalada bajo un
// máximo. Eso resuelve AMBAS causas de un golpe. HEIC casi nunca se decodifica en
// canvas (Safari sí, Chrome no), así que si createImageBitmap falla caemos a
// heic2any (import dinámico → solo se baja cuando hace falta). Si todo falla,
// regresamos el archivo original y dejamos que el server valide (no rompemos).

const MAX_DIM = 1600 // lado mayor en px
const MAX_BYTES = 4.5 * 1024 * 1024 // margen bajo el límite de 5 MB del server
const JPEG_QUALITY = 0.82

const isHeicLike = (f: File): boolean =>
  /image\/hei[cf]/i.test(f.type) || /\.(heic|heif)$/i.test(f.name)

async function decodeToBitmap(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file)
  } catch {
    // El navegador no decodifica este formato (HEIC en Chrome) → heic2any.
    const { default: heic2any } = await import('heic2any')
    const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: JPEG_QUALITY })
    const blob = Array.isArray(out) ? out[0] : out
    return createImageBitmap(blob as Blob)
  }
}

function bitmapToJpeg(bitmap: ImageBitmap, quality: number): Promise<Blob> {
  const scale = Math.min(1, MAX_DIM / Math.max(bitmap.width, bitmap.height))
  const w = Math.max(1, Math.round(bitmap.width * scale))
  const h = Math.max(1, Math.round(bitmap.height * scale))
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas 2d context no disponible')
  // Fondo blanco: PNG/HEIC con transparencia → JPEG no tiene alpha.
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, w, h)
  ctx.drawImage(bitmap, 0, 0, w, h)
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas.toBlob falló'))), 'image/jpeg', quality)
  })
}

/**
 * Devuelve un File listo para subir: JPEG, ≤ ~4.5 MB y lado mayor ≤ 1600 px.
 * - GIF chico se respeta (convertirlo mataría la animación).
 * - JPEG/PNG/WEBP ya chico y soportado pasa tal cual (no recomprime de gusto).
 * - Cualquier otra cosa (HEIC, grande, formato raro) se decodifica y reexporta.
 * Best-effort: ante cualquier fallo regresa el original (el server dará su error).
 */
export async function prepareImageForUpload(file: File): Promise<File> {
  if (file.type === 'image/gif' && file.size <= MAX_BYTES) return file

  const alreadyOk =
    /image\/(png|jpe?g|webp)/i.test(file.type) && file.size <= MAX_BYTES && !isHeicLike(file)
  if (alreadyOk) return file

  try {
    const bitmap = await decodeToBitmap(file)
    let quality = JPEG_QUALITY
    let blob = await bitmapToJpeg(bitmap, quality)
    while (blob.size > MAX_BYTES && quality > 0.4) {
      quality -= 0.15
      blob = await bitmapToJpeg(bitmap, quality)
    }
    bitmap.close?.()
    const base = file.name.replace(/\.[^.]+$/, '') || 'imagen'
    return new File([blob], `${base}.jpg`, { type: 'image/jpeg' })
  } catch {
    return file
  }
}
